import BillLetterJoinFormPageView from 'src/pages/BillLetterJoinFormPage/view'
import React, { useCallback, useEffect, useState } from 'react'
import {
  termsListQueryOption,
  uiInfoQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { useParamsList } from 'src/hooks/useParamsList.hook'
import {
  CertNumberRequestSpace,
  SKTBillLetterJoinSpace,
  SKTBillLetterOtpVerifySpace,
  TermsListSpace,
  termsValueNumberType,
  UIInfoSpace,
  VulnerableVerifySpace,
} from 'src/types/api'
import { FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import useDefaultJoinForm from 'src/hooks/react-hook-form/useDefaultJoinForm'
import { FormObjectType, JoinFormType } from 'src/types/react-hook-form'
import {
  billLetterCertVerifyQuery,
  billLetterJoinQuery,
  certRequestQuery,
  useCustomMutation,
  VulnerableVerifyQuery,
} from 'src/hooks/react-query/useCustomMutation.hook'
import Loading from 'src/components/Loading'
import {
  ERRCODE_SUCCESS,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
} from 'src/types/errcodes'
import { useCountdown } from 'src/hooks/useCountDown.hook'

import {
  already_signup_dataLayer,
  d30_resignup_dataLayer,
  join_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/service/utils/gtmDatalayer'
import { handleBillLetterAppOpen, handleDeviceAppOpen } from 'src/service/utils'
import { parseUiData } from 'src/service/utils/decode'
import { dynamicUiDefaultData } from 'src/service/constants'
import usePcodeMatch from 'src/hooks/usePcodeMatch.hook'
import RenderIf from 'src/components/RenderIf'
import { join_complete_tnk } from 'src/service/utils/tnk'
import VulnerableCheckModal from 'src/components/Modal/_component/VulnerableCheckModal'
import { useModal } from 'src/components/Modal/provider'
import { getTermsErrorMessage } from 'src/hooks/react-hook-form/shared/getTermsErrorMessage'
import { REQUIRED_TERMS_SKT_CONFIRM_MESSAGE } from 'src/constants/terms/messages'

export interface BillLetterJoinFormPageProps {
  registerAttribute: Record<keyof JoinFormType, Partial<FormObjectType>>
  dynamicUiData: Omit<UIInfoSpace.Response, 'UIData'> & { UIData: any }
  otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  }
  otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  }
  onBillLetterJoin: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  }
  registerAttributeExcludeAllTerms: Partial<FormObjectType>[]
  count: string
  otpCertVerifyComplete: boolean
  goToButtonState: boolean
  handleGoToButton: () => void
}

const BillLetterJoinFormPage = () => {
  const { pCode, slotNo } = useParamsList()
  const { bill_letter_03, tnk_01 } = usePcodeMatch()
  const [goToButtonState, setGoToButtonState] = useState(false)
  const { openModal } = useModal()
  const [otpCertVerifyComplete, setOtpCertVerifyComplete] = useState(false)
  const { data: { Body: termsContents = undefined } = {} } =
    useCustomQuery<TermsListSpace.Response>(termsListQueryOption())

  const { data: { Body: { UIData, ...dynamicUi } = dynamicUiDefaultData } = {} } =
    useCustomQuery<UIInfoSpace.Response>(uiInfoQueryOption(pCode as string, slotNo))

  const { mutateAsync: otpRequestMutate } = useCustomMutation<
    CertNumberRequestSpace.Request,
    CertNumberRequestSpace.Response
  >(certRequestQuery())

  const { mutateAsync: billLetterOtpVerifyMutate, isPending: isBillLetterOtpVerifyPending } =
    useCustomMutation<SKTBillLetterOtpVerifySpace.Request, SKTBillLetterOtpVerifySpace.Response>(
      billLetterCertVerifyQuery(),
    )

  const { mutateAsync: billLetterJoinMutate, isPending: isBillLetterJoinIsPending } =
    useCustomMutation<SKTBillLetterJoinSpace.Request, SKTBillLetterJoinSpace.Response>(
      billLetterJoinQuery(),
    )
  const { methods, registerAttribute, allTermsAgreeHandler, requiredTermsList, termsData } =
    useDefaultJoinForm(termsContents)

  const { isPending: isVulnerableVerifyPending, mutateAsync: vulnerableVerifyMutate } =
    useCustomMutation<VulnerableVerifySpace.Request, VulnerableVerifySpace.Response>(
      VulnerableVerifyQuery(),
    )

  const { setFocus, setValue, reset, getValues, resetField } = methods

  const { countStart, count, countStop } = useCountdown({
    countExpiredCallbackFn: () => {
      resetField('ApprovalNumber')
      alert('유효시간이 초과되었습니다.\n인증번호를 다시 받으시기 바랍니다.')
    },
  })
  const joinCompletePageHandler = () => {
    if (bill_letter_03) {
      handleDeviceAppOpen()
    } else {
      handleBillLetterAppOpen()
    }
  }

  const handleCheckVulnerableUser = async () => {
    const Telecom = 0

    return await vulnerableVerifyMutate({ PNumber: getValues('PNumber'), Telecom }).then((res) => {
      const { Vulnerable, AgePopUpYN } = res.Body
      
      const isVulnerable = Vulnerable === 'Y' || AgePopUpYN === 'Y'
      if (!isVulnerable) return true

      return new Promise<boolean>((resolve) => {
        openModal({
          contents: {
            children: (
              <VulnerableCheckModal
                onConfirm={() => {
                  resolve(true)
                }}
                onCancel={() => {
                  resolve(false)
                }}
                telecom={Telecom}
              />
            ),
          },
        })
      })
    })
  }

  //인증 번호 전송
  const otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const { PNumber } = data
      const Telecom = 0

      otpRequestMutate({
        PNumber,
        Telecom,
        PCode: pCode,
        TimeOut: 300,
      })
        .then((res) => {
          const { Body, Header } = res
          switch (Header.ErrCode) {
            case ERRCODE_SUCCESS:
              const { TranID, TimeOut } = Body
              setValue('TranID', TranID)
              setValue('ApprovalNumber', '')
              setOtpCertVerifyComplete(false)
              otp_request_dataLayer()
              alert(
                '입력하신 번호로 인증번호가 전송되었습니다.\n 전송된 인증번호를 입력하여 주세요.',
              )
              countStart(TimeOut)
              setTimeout(() => {
                setFocus('ApprovalNumber')
              }, 300)
              break
            case ERRCODES_OTPCERTREQUEST.Error5timesLimit:
            default:
              alert(Header.ErrMsg)
              countStop()
              break
            case ERRCODES_OTPCERTREQUEST.ErrorAlreadyMember:
              alert(Header.ErrMsg)
              already_signup_dataLayer()
              resetFormItems()
              joinCompletePageHandler()
              break
            case ERRCODES_OTPCERTREQUEST.Error30DaysLimit:
              d30_resignup_dataLayer()
              alert(Header.ErrMsg)
              break
            case ERRCODES_OTPCERTREQUEST.ErrorSpMember:
              alert(Header.ErrMsg)
              resetFormItems()
              break
            case ERRCODES_OTPCERTREQUEST.ErrorPNumber:
            case ERRCODES_OTPCERTREQUEST.ErrorNoTelecom:
            case ERRCODES_OTPCERTREQUEST.ErrorAuthMsgTime:
              alert(Header.ErrMsg)
              break
          }
        })
        .catch((err: Error) => {
          console.error(err)
        })
    },
    error: (errors, event) => {
      const { PNumber } = errors
      const errorMsg = PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      if (!!errorMsg) {
        alert(errorMsg)
      } else if (!!termsErrorMsg) {
        const handleConfirm = window.confirm(termsErrorMsg)
        if (handleConfirm) {
          allTermsAgreeHandler()
          otpCertRequest.submit({ PNumber: getValues('PNumber') })
        } else return
      } else return
      console.warn(errors)
    },
  }

  //확인 버튼
  const otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const { PNumber, TranID, ApprovalNumber } = data

      if (getValues('ApprovalNumber')?.length === 0) return alert('인증번호를 입력해주세요.')

      billLetterOtpVerifyMutate({
        Telecom: 0,
        PNumber,
        TranID,
        ApprovalNumber,
      })
        .then((res) => {
          const { Header } = res
          switch (Header.ErrCode) {
            case ERRCODE_SUCCESS: {
              const allRequiredTermsAgreed = requiredTermsList.every((key) =>
                getValues(key as keyof JoinFormType),
              )
              if (
                !allRequiredTermsAgreed &&
                window.confirm(REQUIRED_TERMS_SKT_CONFIRM_MESSAGE)
              ) {
                allTermsAgreeHandler()
              }
              setOtpCertVerifyComplete(true)
              countStop()
              setTimeout(() => {
                setFocus('allTerms')
              }, 300)
              break
            }
            default:
            case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNoRequest:
              alert(Header.ErrMsg)
              break
            case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumberWrong:
              alert(Header.ErrMsg)
              resetField('ApprovalNumber')
              setTimeout(() => {
                setFocus('ApprovalNumber')
              }, 300)
              break
            case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumber3Wrong:
              alert(Header.ErrMsg)
              resetFormItems()
              break
          }
        })
        .catch((err: Error) => {
          console.error(err)
        })
    },
    error: (errors, event) => {
      const { PNumber } = errors
      const isNotTermsErrorMsg = PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      switch (true) {
        case getValues('TranID') === '':
          alert('인증번호 받기를 먼저 시도하여 주세요.')
          break
        case !!isNotTermsErrorMsg:
          alert(isNotTermsErrorMsg)
          break
        case !!termsErrorMsg:
          const handleConfirm = window.confirm(termsErrorMsg)
          if (handleConfirm) {
            allTermsAgreeHandler()
            otpCertVerify.submit({ ...getValues() })
          } else return
          break
        default:
          return
      }
      console.warn(errors)
    },
  }

  // 가입하기
  const onBillLetterJoin: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const {
        PNumber,
        TranID,
        ApprovalNumber,
        Service,
        PrivacyConsent,
        PrivacyThirdParty,
        SKTPrivacy,
      } = data

      if (getValues('ApprovalNumber')?.length === 0) return alert('인증번호를 입력해주세요.')

      handleCheckVulnerableUser().then((isVulnerable) => {
        if (isVulnerable) {
          handleBillLetterJoinSubmit()
        }
      })

      const handleBillLetterJoinSubmit = () => {
        billLetterJoinMutate({
          Telecom: 0,
          PNumber,
          TranID,
          ApprovalNumber,
          TermsIdx: termsData?.Idx,
          AgreeDetail: {
            ServiceAgree: Number(Service) as termsValueNumberType,
            PrivacyConsentAgree: Number(PrivacyConsent) as termsValueNumberType,
            PrivacyThirdPartyAgree: Number(PrivacyThirdParty) as termsValueNumberType,
            SKTPrivacyAgree: Number(SKTPrivacy) as termsValueNumberType,
            KTPrivacyAgree: 0,
            LGUPPrivacyAgree: 0,
            InsuranceAgree: 0,
            MarketingAgree: 0,
            KTMVNOAgree: 0,
          },
          PCode: pCode,
          AuthKey: '', //reward api authkey (reward 존재 시),
        })
          .then((res) => {
            const { Header } = res
            switch (Header.ErrCode) {
              case ERRCODE_SUCCESS:
                resetFormItems()
                join_complete_dataLayer(PNumber)
                if (tnk_01) {
                  join_complete_tnk()
                }
                const joinCompleteConfirm = window.confirm(
                  '휴대폰쿠폰지갑 안내\n휴대폰에서 모바일쿠폰을 상시 할인 및 특가에 구매할 수 있는 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요.',
                )
                if (joinCompleteConfirm) {
                  joinCompletePageHandler()
                } else return

                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNoRequest:
                alert(Header.ErrMsg)
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumberWrong:
                alert(Header.ErrMsg)
                resetField('ApprovalNumber')
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumber3Wrong:
                alert(Header.ErrMsg)
                resetFormItems()
                break
            }
          })
          .catch((err: Error) => {
            console.error(err)
          })
      }
    },
    error: (errors, event) => {
      const { PNumber, TranID } = errors
      const errorMsg = TranID?.message || PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      switch (true) {
        case getValues('TranID') === '':
          alert('인증번호 받기를 먼저 시도하여 주세요.')
          break
        case !!errorMsg:
          alert(errorMsg)
          break
        case getValues('ApprovalNumber')?.length === 0:
          return alert('인증번호를 입력해주세요.')
        case !otpCertVerifyComplete:
          return alert(`'확인' 버튼을 눌러 인증을 완료해주세요.`)
        case !!termsErrorMsg:
          const handleConfirm = window.confirm(termsErrorMsg)
          if (handleConfirm) {
            allTermsAgreeHandler()
            onBillLetterJoin.submit({ ...getValues() })
          } else return
          break
        default:
          return
      }
      console.warn(errors)
    },
  }

  const registerAttributeExcludeAllTerms: () => Partial<FormObjectType>[] = useCallback(() => {
    const objectEntriesAttribute = Object.entries(registerAttribute)

    return objectEntriesAttribute
      .filter((attributeArray) => attributeArray[0] !== 'allTerms')
      .map((attribute) => attribute[1])
      .filter((element) => element.termsTitle !== undefined && element.termsTitle?.includes('필수'))
  }, [registerAttribute])

  const resetFormItems = () => {
    setFocus('PNumber')
    countStop()
    reset()
  }

  useEffect(() => {
    resetFormItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGoToButton = () => {
    setFocus('PNumber')
    setGoToButtonState(true)
  }

  const props: BillLetterJoinFormPageProps = {
    count,
    dynamicUiData:
      dynamicUiDefaultData.UIData !== UIData && UIData !== ''
        ? { UIData: parseUiData(UIData), ...dynamicUi }
        : dynamicUiDefaultData,
    registerAttribute,
    otpCertVerify,
    otpCertRequest,
    otpCertVerifyComplete,
    onBillLetterJoin,
    goToButtonState,
    handleGoToButton,
    registerAttributeExcludeAllTerms: registerAttributeExcludeAllTerms(),
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={isBillLetterOtpVerifyPending || isBillLetterJoinIsPending || isVulnerableVerifyPending}>
        <Loading />
      </RenderIf>
      <BillLetterJoinFormPageView {...props} />
    </FormProvider>
  )
}

export default BillLetterJoinFormPage
